import { FormStateErrorMessage } from "@/components/form/FormStateErrorMessage";
import { useDispatchEmail } from "@/hooks/useDispatchEmail";
import { useToastifyError } from "@/hooks/useToastifyError";
import { isInvalid } from "@/utils/form";
import { Link } from "@chakra-ui/next-js";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Stack,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
  useToast,
  Spacer,
  Center,
  Grid,
  Container,
} from "@chakra-ui/react";
import { EmailEvents } from "@root/types/email";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import Script from "next/script";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Footer } from "@/components/LandingPage/Footer";
import { snakeCase } from "lodash";
import { useRouter } from "next/router";
import { trpc } from "@/utils/trpc";
import Image from "next/image";
import defaultLogo from "@root/public/images/logos/clarifayo.png";
import heroBg from "@root/public/images/photos/clarifayo_ninho.png";
import { TestimonialsSection } from "@/components/LandingPage/TestimonialsSection";
import { DEFAULT_FREE_TRIAL_DAYS } from "@root/types/freeTrial";
import { PricingBox } from "@/components/Pricing/PricingBox";
import { MonthYearToggle } from "@/components/MonthYearToggle";
import { CardListSkeleton } from "@/components/Skeletons/CardListSkeleton";
import { useDebounce } from "@uidotdev/usehooks";
import { FullPageLoader } from "@/components/FullPageLoader";

const variants = ["canary", "blue", "black"];

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export default function Page() {
  const { locale, query, push } = useRouter();
  const [period, setPeriod] = useState<"month" | "year">("month");
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const { register, handleSubmit, formState, watch, setValue } = useForm();
  const createUserProfileMutation = trpc.profiles.create.useMutation();
  const toast = useToast();
  const email = watch("email");
  const queryPlan = query.plan as string;
  const debouncedEmail = useDebounce(email, 1200);

  const t = useTranslations();
  const { toastifyError } = useToastifyError();
  const dispatchEmail = useDispatchEmail();
  const { data: freeTrial } = trpc.freeTrial.find.useQuery(
    {
      email: debouncedEmail,
    },
    {
      enabled: !!email && isValidEmail(email),
      refetchOnWindowFocus: false,
    }
  );
  const { data: plans, isLoading: plansLoading } = trpc.plan.listAll.useQuery(
    undefined,
    {
      refetchOnWindowFocus: false,
    }
  );

  const freeTrialDays = useMemo(
    () => freeTrial?.days || DEFAULT_FREE_TRIAL_DAYS,
    [freeTrial]
  );

  const onSubmit = async (data: any) => {
    setSubmitting(true);

    try {
      if (!data.selectedPlan) {
        console.warn("selectedPlan", data.selectedPlan);
        toast({
          title: t("errors.selectPlan"),
          status: "error",
          isClosable: true,
        });
        return;
      }

      const plan = plans?.find(
        (p) => p.platformId.toString() === data.selectedPlan.toString()
      );
      if (!plan) {
        console.warn("plan", plan, data.selectedPlan);
        toast({
          title: t("errors.selectPlan"),
          status: "error",
          isClosable: true,
        });
        return;
      }

      // @ts-ignore
      const token = await window.grecaptcha.enterprise.execute(
        "6LdeczUpAAAAAN6CWC-58dT2R9fp3sIGM5ROcQ4k"
      );
      const valid = await (
        await fetch(
          `https://recaptchaenterprise.googleapis.com/v1/projects/my-coach-platform/assessments?key=AIzaSyD8MlrH-gBeGdnRrAmLAKDLCjdOiKP4RDY`,
          {
            method: "POST",
            body: JSON.stringify({
              event: {
                token: token,
                siteKey: "6LdeczUpAAAAAN6CWC-58dT2R9fp3sIGM5ROcQ4k",
              },
            }),
          }
        )
      ).json();
      if (valid.riskAnalysis.score < 0.6) {
        console.error("Recaptcha failed", valid);
        return;
      }

      const result = await createUserProfileMutation.mutateAsync({
        ...data,
        role: snakeCase(plan.role).toUpperCase(),
        language: locale === "en" ? "en" : "pt",
        timezone:
          Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ||
          "Europe/Lisbon",

        joinMailList: !data.joinMailList,
        subscriptionPlan: data.selectedPlan.toString(),
      });
      dispatchEmail({
        event: EmailEvents.Welcome,
        userId: result.id,
        fullName: `${data.firstName} ${data.lastName}`,
        email: data.email,
        language: locale?.toLowerCase() || "pt",
      });

      setRedirecting(true);
      const checkoutUrl = await generateCheckoutUrl(
        plan.platformId.toString(),
        result.id!.toString()
      );

      //automatically sign in the user
      await signIn("credentials", {
        email: data.email,
        password: data.password,
        callbackUrl: checkoutUrl,
      });
      push(checkoutUrl);
    } catch (e: any) {
      if (process.env.NODE_ENV !== "production") {
        console.error(e);
      }
      toastifyError(e);
    } finally {
      setSubmitting(false);
      setRedirecting(false);
    }
  };

  const handlePlanChange = useCallback(
    (planId: string) => {
      setValue("selectedPlan", planId);
    },
    [setValue]
  );

  useEffect(() => {
    handlePlanChange(queryPlan);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPlan]);

  const generateCheckoutUrl = async (planId: string, userId: string) => {
    const plan = plans?.find((p) => p.platformId.toString() === planId);
    if (plan) {
      const response = await fetch("/api/stripe/sign-up-subscribe", {
        method: "POST",
        body: JSON.stringify({
          customerId: userId,
          planId: plan.platformId,
          priceId: plan.id,
        }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data.url;
    }
    return null;
  };

  return (
    <>
      <Head>
        <title>Clarifayo - Sign Up</title>
      </Head>
      <Script src="https://www.google.com/recaptcha/enterprise.js?render=6LdeczUpAAAAAN6CWC-58dT2R9fp3sIGM5ROcQ4k" />
      <FullPageLoader isLoading={redirecting} />
      <Box
        position="relative"
        backgroundImage={`url(${heroBg.src})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment="fixed"
        backdropFilter="blur(10px)"
      >
        <Box className="hero-bg"></Box>

        <Container maxW="1200px">
          <Stack
            direction={["column", "row"]}
            w="full"
            minH="90vh"
            py={{
              base: 4,
              md: 8,
            }}
            px={{
              base: 2,
              md: 16,
            }}
            pb={{
              base: 8,
              md: 16,
            }}
            overflow={"hidden"}
            transition="all 0.3s ease-in-out"
          >
            <Box flex={1} zIndex={1}>
              <Center>
                <Link href="/">
                  <Image
                    src={defaultLogo}
                    width={1080 / 2}
                    height={1080 / 2}
                    alt="logo"
                    style={{
                      objectFit: "cover",
                      height: "80px",
                      width: "auto",
                    }}
                  />
                </Link>
              </Center>
              <Spacer my={6} />
              <Card
                w="full"
                boxShadow={"2xl"}
                bgColor="brand.100"
                color="brand.1000"
                rounded="xl"
              >
                <CardHeader>
                  <HStack w="full" justifyContent={"space-between"}>
                    <Box></Box>
                    {/* <Heading size="md" color="brand.1000" fontFamily={"Gotham"}>
                      {t("SignUp.register")}
                    </Heading> */}
                    <HStack>
                      <Button
                        variant="link"
                        color="brand.1000"
                        onClick={() =>
                          signIn("auth0", {
                            callbackUrl: `${window.location.origin}/router`,
                          })
                        }
                      >
                        <Text fontSize="sm">
                          {t("SignUp.alreadyHaveAccount")}{" "}
                          <strong>Sign in</strong>
                        </Text>
                      </Button>
                    </HStack>
                  </HStack>
                </CardHeader>
                <CardBody color="white">
                  <Stack direction={["column", "row"]} gap={4} w="full" mb={4}>
                    <FormControl isInvalid={isInvalid(formState, "email")}>
                      <FormLabel
                        fontSize="sm"
                        textTransform={"uppercase"}
                        color="brand.1000"
                      >
                        {t("email")}
                      </FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="text"
                        placeholder="e.g. john@gmail.com"
                        {...register("email", {
                          required: true,
                          pattern: /\S+@\S+\.\S+/,
                        })}
                      />
                      <FormStateErrorMessage
                        formState={formState}
                        field="email"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("SignUp.phoneNumber")}</FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="tel"
                        placeholder="e.g. +351 999 888 777"
                        {...register("phone", { required: true })}
                      />
                    </FormControl>
                  </Stack>
                  <Stack direction={["column", "row"]} gap={4} w="full" mb={4}>
                    <FormControl isInvalid={isInvalid(formState, "firstName")}>
                      <FormLabel
                        fontSize="sm"
                        textTransform={"uppercase"}
                        color="brand.1000"
                      >
                        {t("firstName")}
                      </FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="text"
                        placeholder="e.g. John"
                        {...register("firstName", { required: true })}
                      />
                      <FormStateErrorMessage
                        formState={formState}
                        field="firstName"
                      />
                    </FormControl>
                    <FormControl isInvalid={isInvalid(formState, "lastName")}>
                      <FormLabel
                        fontSize="sm"
                        textTransform={"uppercase"}
                        color="brand.1000"
                      >
                        {t("lastName")}
                      </FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="text"
                        placeholder="e.g. Doe"
                        {...register("lastName", { required: true })}
                      />
                      <FormStateErrorMessage
                        formState={formState}
                        field="lastName"
                      />
                    </FormControl>
                  </Stack>

                  <Stack direction={["column", "row"]} w="full" gap={4} mb={8}>
                    <FormControl isInvalid={isInvalid(formState, "email")}>
                      <FormLabel
                        fontSize="sm"
                        textTransform={"uppercase"}
                        color="brand.1000"
                      >
                        {t("password")}
                      </FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="password"
                        {...register("password", {
                          required: true,
                        })}
                      />
                      <FormStateErrorMessage
                        formState={formState}
                        field="email"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel
                        fontSize="sm"
                        textTransform={"uppercase"}
                        color="brand.1000"
                      >
                        {t("SignUp.confirmPassword")}
                      </FormLabel>
                      <Input
                        bgColor="brand.1000"
                        type="password"
                        {...register("confirmPassword", {
                          required: true,
                        })}
                      />
                    </FormControl>
                  </Stack>
                  <MonthYearToggle
                    value={period}
                    onChange={setPeriod}
                    justifyContent={"flex-end"}
                  />
                  <Spacer my={2} />
                  <Stack
                    direction={["column", "row"]}
                    w="full"
                    h="full"
                    flexWrap={"wrap"}
                    py={8}
                    rounded="xl"
                    gap={2}
                  >
                    {plansLoading ? (
                      <Grid gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                        <CardListSkeleton
                          isLoading={plansLoading}
                          quantity={3}
                        />
                      </Grid>
                    ) : (
                      plans?.map((plan, index) => (
                        <PricingBox
                          key={plan.id}
                          oldPrice={
                            period === "month"
                              ? plan.oldMonthPrice.toFixed(2)
                              : plan.oldYearPrice.toFixed(2)
                          }
                          price={
                            period === "month"
                              ? plan.monthPrice.toFixed(2)
                              : plan.yearPrice.toFixed(2)
                          }
                          name={plan.name}
                          description={plan.description}
                          features={
                            index === 2
                              ? plan.features.slice(0, 2)
                              : [plan.features[0]]
                          }
                          variant={
                            variants[index % variants.length] as
                              | "canary"
                              | "blue"
                              | "black"
                          }
                          platformId={plan.platformId.toString()}
                          watch={watch}
                          freeTrialDays={freeTrialDays}
                          cardProps={{
                            flex: 1,
                          }}
                          onSelect={() =>
                            handlePlanChange(plan.platformId.toString())
                          }
                        />
                      ))
                    )}
                  </Stack>

                  <VStack my={4} w="full" alignItems="flex-start" gap={4}>
                    <HStack>
                      <Checkbox variant="black" {...register("joinMailList")}>
                        <Text fontSize="sm" color="brand.1000">
                          {t("SignUp.joinMailList")}
                        </Text>
                      </Checkbox>
                    </HStack>

                    <HStack>
                      <Checkbox
                        variant="black"
                        {...register("acceptTerms", { required: true })}
                      >
                        <Text fontSize="sm" color="brand.1000">
                          {t("SignUp.iHaveReadAndAccept")}{" "}
                          <Link href="/terms" w="full" target="_blank">
                            <Text as="span" textDecoration={"underline"}>
                              {t("SignUp.termsAndConditions")}
                            </Text>
                          </Link>
                        </Text>
                      </Checkbox>
                    </HStack>
                    <HStack>
                      <Checkbox
                        {...register("acceptPolicy", { required: true })}
                        variant="black"
                      >
                        <Text fontSize="sm" color="brand.1000">
                          {t("SignUp.iHaveReadAndAccept")}{" "}
                          <Link href="/privacy" w="full" target="_blank">
                            <Text as="span" textDecoration={"underline"}>
                              {t("privacyAndCookiePolicy")}
                            </Text>
                          </Link>
                        </Text>
                      </Checkbox>
                    </HStack>
                    <Button
                      mt={4}
                      variant="secondary"
                      isDisabled={submitting}
                      w="full"
                      onClick={handleSubmit(onSubmit)}
                    >
                      {submitting
                        ? t("SignUp.registering")
                        : t("SignUp.register")}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </Box>
          </Stack>
        </Container>
      </Box>
      <TestimonialsSection />
      <Footer />
    </>
  );
}
